import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, tokens, type StoredUser } from '@/api/client'
import type { PortalRole } from '@/api/types'

// Mirrors security.ROLE_RANK on the API. "Technician" was missing here after
// the 7 Aug 2026 rename from "Crew", so hasRole('Technician') asked for a rung
// that did not exist and refused everyone -- unnoticed until /certs became the
// first route to require it. "Crew" stays for sessions stored before the
// rename, as it does on the server.
const ROLE_RANK: Record<string, number> = {
  Technician: 1,
  Crew: 1,
  Supervisor: 2,
  Office: 3,
  Admin: 4,
}

interface AuthValue {
  user: StoredUser | null
  isAuthenticated: boolean
  /** Field sign-in: employee number + PIN. */
  signIn: (employeeNo: string, pin: string) => Promise<void>
  /** Office sign-in: email + password. Entra ID SSO replaces this later. */
  signInWithPassword: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  hasRole: (minimum: PortalRole) => boolean
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  // Seed from localStorage so a reload — or a PWA relaunch after the OS killed
  // the tab — does not bounce a supervisor back to the login screen mid-shift.
  const [user, setUser] = useState<StoredUser | null>(() => tokens.user())

  const signIn = useCallback(async (employeeNo: string, pin: string) => {
    const res = await api.login({
      employee_no: employeeNo.trim().toUpperCase(),
      pin,
      device_label: navigator.userAgent.slice(0, 100),
    })
    setUser({
      employee_id: res.employee_id,
      employee_no: res.employee_no,
      display_name: res.display_name,
      role: res.role,
    })
  }, [])

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const res = await api.loginWithPassword({
      email: email.trim().toLowerCase(),
      password,
      device_label: navigator.userAgent.slice(0, 100),
    })
    setUser({
      employee_id: res.employee_id,
      employee_no: res.employee_no,
      display_name: res.display_name,
      role: res.role,
    })
  }, [])

  const signOut = useCallback(async () => {
    await api.logout()
    setUser(null)
  }, [])

  const hasRole = useCallback(
    (minimum: PortalRole) =>
      (ROLE_RANK[user?.role ?? ''] ?? 0) >= (ROLE_RANK[minimum] ?? 99),
    [user],
  )

  const value = useMemo<AuthValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      signIn,
      signInWithPassword,
      signOut,
      hasRole,
    }),
    [user, signIn, signInWithPassword, signOut, hasRole],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
